/**
 * Mirrors admin Overview: 4 stat cards + chart panels + deals table — premium minimal skeleton.
 */

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@hooks/useLanguage';
import {
  SkeletonBreath,
  SkeletonCircle,
  SkeletonPanel,
  SkeletonText,
} from './skeletonPrimitives';

const DEALS_PREVIEW_ROWS = 5;
const DEALS_PREVIEW_COLS = 6;

const cardShell = 'rounded-card border border-slate-200/50 bg-white';

export interface OverviewSkeletonProps {
  className?: string;
}

export function OverviewSkeleton({ className = '' }: OverviewSkeletonProps) {
  const { t } = useTranslation();
  const { isRtl } = useLanguage();

  return (
    <div
      className={clsx('font-sans animate-pulse', className)}
      dir={isRtl ? 'rtl' : 'ltr'}
      aria-busy="true"
      aria-live="polite"
      aria-label={t('common.loading', { defaultValue: 'Loading…' })}
    >
      <SkeletonBreath>
        <SkeletonText className="w-[min(45%,320px)]" />
        <SkeletonText className="mt-2 w-[min(70%,420px)] max-w-full" />

        <section className="mb-8 mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-hidden>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className={clsx(cardShell, 'p-6 sm:p-8')}>
              <div className="flex items-center gap-3">
                <SkeletonCircle className="size-10" />
                <SkeletonText className="min-w-0 flex-1 max-w-[140px]" />
              </div>
              <SkeletonText className="mt-4 w-24 sm:w-28" />
            </div>
          ))}
        </section>

        <section className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" aria-hidden>
          <div className={clsx(cardShell, 'p-6 sm:p-8')}>
            <SkeletonText className="w-[min(80%,240px)]" />
            <SkeletonPanel className="mt-4 min-h-[260px] w-full" />
          </div>
          <div className={clsx(cardShell, 'p-6 sm:p-8')}>
            <SkeletonText className="w-[min(80%,240px)]" />
            <SkeletonPanel className="mt-4 min-h-[260px] w-full" />
          </div>
        </section>

        <section aria-hidden>
          <SkeletonText className="mb-4 w-[min(55%,240px)]" />
          <div className={clsx('overflow-hidden', cardShell)}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {Array.from({ length: DEALS_PREVIEW_COLS }, (_, i) => (
                      <th key={i} className="py-3 px-4 text-start">
                        <SkeletonText className="w-16" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: DEALS_PREVIEW_ROWS }, (_, row) => (
                    <tr
                      key={row}
                      className={clsx(
                        'border-b border-slate-100/80 last:border-0',
                        row % 2 === 1 ? 'bg-slate-50/50' : '',
                      )}
                    >
                      {Array.from({ length: DEALS_PREVIEW_COLS }, (_, col) => (
                        <td key={col} className="py-3 px-4">
                          <SkeletonText className={col === 0 ? 'w-6' : 'w-full max-w-[100px]'} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </SkeletonBreath>
    </div>
  );
}
